import React, { useMemo, useCallback, useState, useEffect } from "react";
import {
  FormProvider,
  useForm,
  type SubmitHandler,
  type Resolver,
  useWatch,
} from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { FieldRenderer } from "../../../components/form/FieldRenderer";
import { product, paymentTop, paymentDates } from "./config";
import { addMspProductFormSchema, AddMSPProductFormValues } from "./schema";
import { Field, Input } from "../../../components/form/atoms";
import { SECTION_CARD_CLASS } from "../../../constants/mspAndCustomerForm";
import { useAfterDiscount } from "../../../utils/calculations";
import { AddMspProductFormProps } from "./types";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import {
  addMspProduct,
  fetchMSPPlans,
  fetchProducts,
} from "../services/mspService";
import { usePlanByLicenseCount } from "../../../hooks/usePlanByLicenseCount";

type Opt = { label: string; value: number | string };

export default function AddMspProductForm({
  onProductAdded,
  onCancel,
  allowedProducts,
}: AddMspProductFormProps) {
  const [productOpts, setProductOpts] = useState<Opt[]>([]);
  const [selectedLicenseCount, setSelectedLicenseCount] = useState<number>();
  const [plans, setPlans] = useState<[]>([]);
  const [planOpts, setPlanOpts] = useState<Opt[]>([]);
  const [billingFrequencyOpts, setBillingFrequencyOpts] = useState<Opt[]>([]);
  const dispatch = useAppDispatch();
  const resolver = useMemo(
    () =>
      zodResolver(addMspProductFormSchema) as Resolver<AddMSPProductFormValues>,
    []
  );
  const selectedMspId = useAppSelector(
    (state) => state.selectedMsp.selectedMspId
  );

  const DEFAULT_VALUES = {
    plan_price_id: undefined,
    no_of_licenses: undefined,
    total_paid_amount: undefined,
    discount: undefined,
    payment_mode: undefined,
    transaction_ref: undefined,
    date_of_payment: undefined,
  };

  const methods = useForm<AddMSPProductFormValues>({
    resolver,
    mode: "onBlur",
    shouldFocusError: false,
    shouldUnregister: false,
    defaultValues: DEFAULT_VALUES,
  });

  const {
    handleSubmit,
    reset,
    register,
    control,
    setValue,
    formState: { errors, isSubmitting },
  } = methods;

  const [selectedProductId] = useWatch({
    control,
    name: ["product"],
  }) as [string | undefined];

  const [totalPaidAmount, discount] = useWatch({
    control,
    name: ["total_paid_amount", "discount"],
  });

  const { matchedPlan, pricingOptions, getPriceById } = usePlanByLicenseCount(
    plans,
    selectedLicenseCount
  );

  const [selectedPriceId, setSelectedPriceId] = useState<number | null>(null);

  const afterDiscount = useAfterDiscount(totalPaidAmount, discount);

  // ---- Fetch products on mount (filter by allowedProducts if provided)
  useEffect(() => {
    (async () => {
      try {
        const products: any[] = await fetchProducts(); // [{ id, product_name, ... }, ...]
        // If allowedProducts provided, do a case-insensitive check against product_name
        let filtered = products;
        if (
          allowedProducts &&
          Array.isArray(allowedProducts) &&
          allowedProducts.length > 0
        ) {
          const allowedSet = new Set(
            allowedProducts.map((p) => String(p).toUpperCase().trim())
          );
          filtered = products.filter((p) =>
            allowedSet.has(String(p.product_name).toUpperCase().trim())
          );
        }

        // Map to options (value can be number or string depending on your Select component expectations)
        const opts = filtered.map((p: any) => ({
          label: p.product_name,
          value: p.id,
        }));

        setProductOpts(opts);

        // If only one allowed product, auto-select it (use string to match how your form stores product)
        if (opts.length === 1) {
          // ensure form control 'product' matches the option value type you use elsewhere
          // here we set as String because useWatch returned product as string in your component
          setValue("product", String(opts[0].value));
        }
      } catch (e) {
        console.error("Failed to load products", e);
      }
    })();
    // include allowedProducts and setValue in deps so effect re-runs when allowed list changes
  }, [allowedProducts, setValue]);

  // ---- When product changes, reset plan + price and fetch plans
  useEffect(() => {
    // When product becomes empty/cleared - fully reset form defaults
    if (!selectedProductId) {
      // clear local product-dependent state
      setPlans([]);
      setPlanOpts([]);
      setBillingFrequencyOpts([]);
      setSelectedLicenseCount(undefined);
      setSelectedPriceId(null);

      // reset form to defaults (no product)
      reset(DEFAULT_VALUES);
      return;
    }

    // Immediately reset form to defaults BUT keep product set to the newly selected product.
    // This prevents stale plan/price/license values from briefly showing.
    reset({
      ...DEFAULT_VALUES,
      product: String(selectedProductId), // match how your form stores product
    });

    // clear local product-dependent state (so UI shows empty options while fetching)
    setPlans([]);
    setPlanOpts([]);
    setBillingFrequencyOpts([]);
    setSelectedLicenseCount(undefined);
    setSelectedPriceId(null);

    // now fetch plans for the new product
    (async () => {
      try {
        const fetchedPlans = await fetchMSPPlans(Number(selectedProductId));
        setPlans(fetchedPlans);
        setPlanOpts(
          fetchedPlans.map((pl: any) => ({ label: pl.plan_name, value: pl.id }))
        );
      } catch (e) {
        console.error("Failed to load plans", e);
      }
    })();
  }, [selectedProductId, reset, setValue]);

  // when intervals change, default to first option for now as there is only one option
  useEffect(() => {
    setSelectedPriceId(pricingOptions[0]?.priceId ?? null);
    const unitAmount =
      getPriceById(pricingOptions[0]?.priceId)?.unitAmount ?? 0;
    const licenseCount = selectedLicenseCount ?? 0;
    setValue("total_paid_amount", unitAmount * licenseCount);
  }, [pricingOptions, selectedLicenseCount]);

  //If matched plan changes (based on license count), show billing frequency options
  useEffect(() => {
    console.log("Matched plan changed:", matchedPlan);
    // If no license count or no match, clear plan fields
    if (!selectedLicenseCount || !matchedPlan) {
      setValue("plan", "");
      return;
    }

    // Set matched plan
    setValue("plan", String(matchedPlan.id));
    console.log("Setting matched plan ID:", matchedPlan.id);
    console.log("Available intervals", pricingOptions);

    setBillingFrequencyOpts(
      pricingOptions.map((opts: any) => ({
        label: opts.interval,
        value: opts.priceId,
      }))
    );
  }, [matchedPlan?.id, selectedLicenseCount, getPriceById, setValue]);

  //Auto-select first billing frequency option
  useEffect(() => {
    setValue("billing_frequency", String(pricingOptions[0]?.priceId) ?? null);
  }, [pricingOptions]);

  // Form submission handler
  const onSubmit: SubmitHandler<AddMSPProductFormValues> = useCallback(
    async (formData) => {
      try {
        const payload = {
          plan_price_id: formData.billing_frequency,
          no_of_licenses: formData.no_of_licenses || 1,
          total_paid_amount: formData.total_paid_amount,
          discount: formData.discount || "0",
          payment_mode: formData.payment_mode,
          transaction_ref: formData.transaction_ref || "",
          date_of_payment: formData.date_of_payment,
          msp_id: selectedMspId,
        };
        await addMspProduct(payload);
        reset();
        onCancel && onCancel();
        onProductAdded && onProductAdded();
      } catch (error) {
        console.error("Failed to add MSP product:", error);
      }
    },
    [dispatch, reset]
  );

  const productSectionWithOptions = useMemo(() => {
    return product.map((f) => {
      if (f.name === "product") {
        return {
          ...f,
          options: productOpts,
        };
      }
      if (f.name === "plan") {
        return {
          ...f,
          options: planOpts,
          disabled: Boolean(matchedPlan),
          placeholder: matchedPlan
            ? "Auto-selected by license count"
            : "Select plan",
          // placeholder: selectedProductId ? "Select plan" : "Select product first",
        };
      }

      if (f.name === "billing_frequency") {
        console.log("Setting billing frequency options:", billingFrequencyOpts);
        return {
          ...f,
          options: billingFrequencyOpts,
          suffixText: selectedPriceId ? (
            <span>
              <span className="font-bold">
                ${getPriceById(selectedPriceId)?.unitAmount || "0.00"}
              </span>{" "}
              per unit
            </span>
          ) : null,
          // placeholder: selectedPlanId ? (loading.prices ? "Loading prices..." : "Select plan price") : "Select plan first",
        };
      }

      if (f.name === "no_of_licenses") {
        console.log("Adding onBlur to no_of_licenses field");
        return {
          ...f,
          onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
            console.log("no_of_licenses onBlur event:", e.target.value);
            const value = e.target.value ? Number(e.target.value) : undefined;
            setSelectedLicenseCount(value);
          },
          suffixText: selectedPriceId ? (
            <span>
              <span className="font-bold">
                ${getPriceById(selectedPriceId)?.unitAmount || "0.00"}
              </span>{" "}
              per unit
            </span>
          ) : null,
        };
      }
      return f;
    });
  }, [product, productOpts, planOpts, billingFrequencyOpts]);

  // Render functions for organization
  const renderHeader = () => (
    <div className="mb-4 flex items-start justify-between p-2">
      <div>
        <h2 className="text-[26px] font-semibold text-[#4F4F4F]">
          Add Product
        </h2>
      </div>
    </div>
  );

  const renderFormSection = (title: string, config: any[]) => (
    <>
      <h3 className="text-[15px] font-bold text-[#2B2B2B]">{title}</h3>
      <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-2">
        {config.map((f) => (
          <FieldRenderer key={f.name} cfg={f} />
        ))}
      </div>
    </>
  );

  const renderPaymentDetails = () => (
    <div className="mt-12 rounded-sm border border-[#E6E8EE] bg-white p-6 shadow-[0_8px_24px_rgba(0,0,0,0.06)]">
      <h3 className="text-[15px] font-bold text-[#2B2B2B]">Payment Details</h3>

      <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
        <Field
          label="Total amount ($)"
          required
          error={errors.total_paid_amount?.message}
        >
          <Input
            inputMode="decimal"
            autoComplete="off"
            {...register("total_paid_amount")}
          />
        </Field>

        <Field label="Discount(%)" error={errors.discount?.message}>
          <Input
            inputMode="decimal"
            autoComplete="off"
            {...register("discount")}
          />
          <div className="mt-2 text-[12.5px] font-semibold text-[#2B2B2B]">
            After discount: <span className="font-bold">${afterDiscount}</span>
          </div>
        </Field>

        {paymentTop
          .filter(
            (f) => f.name === "payment_mode" || f.name === "transaction_ref"
          )
          .map((f) => (
            <FieldRenderer key={f.name} cfg={f} />
          ))}
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-4">
        {paymentDates.map((f) => (
          <FieldRenderer key={f.name} cfg={f} />
        ))}
      </div>
    </div>
  );
  return (
    <>
      <FormProvider {...methods}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="p-6"
          autoComplete="off"
        >
          {renderHeader()}

          <div className={SECTION_CARD_CLASS}>
            <div>{renderFormSection("", productSectionWithOptions)}</div>

            {renderPaymentDetails()}

            <div className="mt-8 flex justify-center">
              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex min-w-[180px] items-center justify-center rounded-[6px] bg-[#1D58E2] px-8 py-3 text-[15px] font-semibold text-white shadow hover:brightness-110 disabled:opacity-50 transition-all duration-200"
              >
                {isSubmitting ? "CREATING..." : "ADD NOW"}
              </button>
            </div>
          </div>
        </form>
      </FormProvider>
    </>
  );
}
